import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Alert,
  LinearProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Comment as CommentIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  Poll as PollIcon,
  Inventory as InventoryIcon,
  Message as MessageIcon,
  CheckCircle as CheckCircleIcon,
  Archive as ArchiveIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';
import { useTheme, useMediaQuery } from '@mui/material';
import LocationPicker from '../components/LocationPicker';
import { getStatusLabel as getReportStatusLabelUtil } from '../utils/statusLabels';
import { Project, ProjectTask, ProjectActivity, TaskComment } from '../services/mockProjects';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const { hasPermission, isAdmin, isAgence, isSOBEBRA } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [activities, setActivities] = useState<ProjectActivity[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [openInventoryDialog, setOpenInventoryDialog] = useState(false);
  const [openEditInventoryDialog, setOpenEditInventoryDialog] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<any>(null);
  const [reviewFiles, setReviewFiles] = useState<File[]>([]);
  const [availableAssets, setAvailableAssets] = useState<any[]>([]);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [openEditReportDialog, setOpenEditReportDialog] = useState(false);
  const [openViewReportDialog, setOpenViewReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [projectReports, setProjectReports] = useState<any[]>([]);
  const [reportFiles, setReportFiles] = useState<File[]>([]);
  const [reportData, setReportData] = useState({
    title: '',
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    status: 'draft',
  });
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium',
  });
  const [commentData, setCommentData] = useState({
    content: '',
  });
  const [inventoryData, setInventoryData] = useState({
    asset_id: '',
    location: '',
    notes: '',
  });
  const [postMortem, setPostMortem] = useState('');

  useEffect(() => {
    // Attendre que la session soit disponible avant de charger
    if (session && id) {
      fetchProject();
      // Charger les ressources disponibles
      import('../services/mockInventory').then(({ mockAssets }) => {
        setAvailableAssets(mockAssets);
      });
      // Charger les rapports du projet
      fetchProjectReports();
      loadUsers();
    }
  }, [id, session]);

  const loadUsers = async () => {
    try {
      const { users: allUsers } = await api.users.getAll();
      setUsers(allUsers);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  const fetchProjectReports = async () => {
    try {
      const response = await api.reports.getAll();
      const reports = (response.reports || []).filter((r: any) => r.project_id === id);
      setProjectReports(reports);
    } catch (error) {
      console.error('Erreur lors de la récupération des rapports:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await api.projects.getById(id);
      if (response.project) {
        const p = response.project as any;
        setProject({
          id: p.id,
          title: p.title,
          description: p.description || '',
          objectives: p.objectives || '',
          status: p.status,
          priority: p.priority,
          created_by: p.created_by,
          created_by_name: p.created_by_name || '',
          manager_agence: p.manager_agence,
          manager_agence_name: p.manager_agence_name || '',
          contact_soebra: p.contact_soebra,
          contact_soebra_name: p.contact_soebra_name || '',
          assigned_to: p.assigned_to,
          assigned_to_name: p.assigned_to_name || '',
          start_date: p.start_date ? new Date(p.start_date).toISOString().split('T')[0] : undefined,
          due_date: p.due_date ? new Date(p.due_date).toISOString() : undefined,
          budget: p.budget ? parseFloat(p.budget) : undefined,
          zones: p.zones || [],
          kpi_objectives: p.kpi_objectives || [],
          created_at: p.created_at,
          updated_at: p.updated_at || p.created_at,
          location: p.location,
          post_mortem: p.post_mortem,
          is_archived: p.is_archived || false,
        });
        setTasks((response.tasks || []).map((t: any) => ({
          ...t,
          assigned_to_name: t.assigned_to_user?.name || '',
        })));
        setPostMortem(p.post_mortem || '');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du projet:', error);
    }
  };

  const handleCreateTask = async () => {
    if (!taskData.title || !project) return;

    try {
      const response = await api.tasks.create(project.id, {
        title: taskData.title,
        description: taskData.description || undefined,
        assigned_to: taskData.assigned_to || undefined,
        due_date: taskData.due_date || undefined,
      });

      const newTask = {
        ...response.task,
        assigned_to_name: response.task.assigned_to_user?.name || '',
      };

      setTasks([...tasks, newTask as ProjectTask]);
      addActivity('task_added', `Tâche "${taskData.title}" ajoutée`);
      setTaskData({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium' });
      setOpenTaskDialog(false);
    } catch (error: any) {
      console.error('Erreur lors de la création de la tâche:', error);
      alert('Erreur lors de la création de la tâche: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleAddComment = () => {
    if (!commentData.content || !selectedTask) return;

    const newComment: TaskComment = {
      id: 'comment-' + Date.now(),
      task_id: selectedTask.id,
      user_id: user?.id || '',
      user_name: user?.name || '',
      content: commentData.content,
      created_at: new Date().toISOString(),
    };

    const updatedTasks = tasks.map(task =>
      task.id === selectedTask.id
        ? { ...task, comments: [...(task.comments || []), newComment] }
        : task
    );

    setTasks(updatedTasks);
    setCommentData({ content: '' });
    setOpenCommentDialog(false);
    setSelectedTask(null);
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: ProjectTask['status']) => {
    try {
      await api.tasks.update(taskId, { status: newStatus });
      const updatedTasks = tasks.map(task => {
        if (task.id === taskId) {
          const taskTitle = task.title;
          addActivity('status_changed', `Statut de la tâche "${taskTitle}" changé à "${newStatus}"`);
          return { ...task, status: newStatus, updated_at: new Date().toISOString() };
        }
        return task;
      });
      setTasks(updatedTasks);
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour de la tâche:', error);
      alert('Erreur lors de la mise à jour: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleArchiveProject = async () => {
    if (!project) return;
    try {
      await api.projects.update(project.id, { status: 'archived', is_archived: true });
      setProject({ ...project, status: 'archived', is_archived: true });
      addActivity('archived', 'Projet archivé');
      setOpenArchiveDialog(false);
    } catch (error: any) {
      console.error('Erreur lors de l\'archivage:', error);
      alert('Erreur lors de l\'archivage: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleApproveProject = async () => {
    if (!project) return;
    try {
      await api.projects.update(project.id, { status: 'completed' });
      setProject({ ...project, status: 'completed' });
      addActivity('approved', 'Projet approuvé par SOBEBRA');
      setOpenReviewDialog(false);
    } catch (error: any) {
      console.error('Erreur lors de l\'approbation:', error);
      alert('Erreur lors de l\'approbation: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleCreateReport = async () => {
    if (!reportData.title || !project) return;
    try {
      await api.reports.create({
        project_id: project.id,
        title: reportData.title,
        description: reportData.description || undefined,
        location: reportData.location || undefined,
        latitude: reportData.latitude ? parseFloat(reportData.latitude) : undefined,
        longitude: reportData.longitude ? parseFloat(reportData.longitude) : undefined,
        status: reportData.status || 'draft',
      });
      setOpenReportDialog(false);
      setReportData({
        title: '',
        description: '',
        location: '',
        latitude: '',
        longitude: '',
        status: 'draft',
      });
      setReportFiles([]);
      fetchProjectReports();
      addActivity('report_added', `Rapport "${reportData.title}" ajouté`);
    } catch (error) {
      console.error('Erreur lors de la création du rapport:', error);
    }
  };

  const handleUpdateReport = async () => {
    if (!selectedReport || !reportData.title) return;
    try {
      // Simuler la mise à jour
      const updatedReports = projectReports.map(r =>
        r.id === selectedReport.id
          ? {
              ...r,
              ...reportData,
              latitude: reportData.latitude ? parseFloat(reportData.latitude) : r.latitude,
              longitude: reportData.longitude ? parseFloat(reportData.longitude) : r.longitude,
            }
          : r
      );
      setProjectReports(updatedReports);
      setOpenEditReportDialog(false);
      setSelectedReport(null);
      setReportData({
        title: '',
        description: '',
        location: '',
        latitude: '',
        longitude: '',
        status: 'draft',
      });
      setReportFiles([]);
      addActivity('report_updated', `Rapport "${reportData.title}" modifié`);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du rapport:', error);
    }
  };

  const handleViewReport = (report: any) => {
    setSelectedReport(report);
    setOpenViewReportDialog(true);
  };

  const handleDownloadReport = (report: any) => {
    // Générer un PDF ou document du rapport
    const reportContent = `
RAPPORT D'ACTIVITÉ
==================

Titre: ${report.title}
Projet: ${report.project_title || project?.title}
Créé par: ${report.created_by_name}
Date: ${new Date(report.created_at).toLocaleString('fr-FR')}
Statut: ${getReportStatusLabel(report.status)}

Description:
${report.description || 'Aucune description'}

Localisation: ${report.location || 'Non spécifiée'}
Coordonnées GPS: ${report.latitude && report.longitude ? `${report.latitude}, ${report.longitude}` : 'Non spécifiées'}

${report.submitted_at ? `Soumis le: ${new Date(report.submitted_at).toLocaleString('fr-FR')}` : ''}
${report.pre_validated_by_name ? `Pré-validé par: ${report.pre_validated_by_name} le ${new Date(report.pre_validated_at).toLocaleString('fr-FR')}` : ''}
${report.approved_by_name ? `Approuvé par: ${report.approved_by_name} le ${new Date(report.approved_at).toLocaleString('fr-FR')}` : ''}
    `.trim();

    const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rapport_${report.id}_${new Date().toISOString().split('T')[0]}.txt`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getReportStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      'draft': 'Brouillon',
      'submitted': 'Soumis',
      'pre_validated': 'Pré-validé',
      'approved': 'Approuvé',
      'rejected': 'Rejeté',
    };
    return statusMap[status] || status;
  };

  const getReportStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'submitted':
      case 'pre_validated':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const addActivity = (action: string, details: string) => {
    const newActivity: ProjectActivity = {
      id: 'activity-' + Date.now(),
      project_id: project?.id || '',
      user_id: user?.id || '',
      user_name: user?.name || '',
      action,
      details,
      timestamp: new Date().toISOString(),
    };
    setActivities([newActivity, ...activities]);
  };

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      'pending': 'En Attente',
      'in_progress': 'En Cours',
      'review': 'En Revue SOBEBRA',
      'completed': 'Terminé',
      'cancelled': 'Annulé',
      'archived': 'Archivé',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'done':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'review':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: string): string => {
    const priorityMap: Record<string, string> = {
      'low': 'Basse',
      'medium': 'Moyenne',
      'high': 'Haute',
      'urgent': 'Urgente',
    };
    return priorityMap[priority] || priority;
  };

  const getTaskStatusLabel = (status: string) => {
    switch (status) {
      case 'todo':
        return 'À faire';
      case 'in_progress':
        return 'En cours';
      case 'review':
        return 'En revue';
      case 'done':
        return 'Terminée';
      default:
        return status;
    }
  };

  // Charger les inventaires depuis mockInventory
  const [projectInventory, setProjectInventory] = useState<any[]>([]);
  
  useEffect(() => {
    if (project && availableAssets.length > 0) {
      const projectAssets = availableAssets.filter(asset => asset.current_project_id === project.id);
      setProjectInventory(projectAssets.map(asset => ({
        id: asset.id,
        name: asset.name,
        location: asset.location || '-',
        status: asset.status,
        category: asset.category,
        qr_code: asset.qr_code,
      })));
    }
  }, [project, availableAssets]);

  if (!project) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')}>
          Retour
        </Button>
        <Typography variant="h6" sx={{ mt: 2 }}>
          Projet non trouvé
        </Typography>
      </Box>
    );
  }

  // Les rapports sont maintenant chargés dynamiquement via fetchProjectReports()
  const projectSurveys = [
    { id: 'survey-1', name: 'Sondage Consommateurs - Marchés Cotonou', responses: 45, status: 'active', form_id: 'form-1' },
  ];

  const taskProgress = {
    total: tasks.length,
    done: tasks.filter(t => t.status === 'done').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    todo: tasks.filter(t => t.status === 'todo').length,
  };

  return (
    <Box>
      <Box 
        display="flex" 
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        mb={3}
        gap={{ xs: 2, sm: 0 }}
      >
        <IconButton onClick={() => navigate('/projects')} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ ml: { xs: 0, sm: 2 }, flex: 1, width: '100%' }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, wordBreak: 'break-word' }}>
            {project.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip label={getStatusLabel(project.status)} color={getStatusColor(project.status) as any} size="small" />
            <Chip label={getPriorityLabel(project.priority)} size="small" />
            {project.is_archived && <Chip label="Archivé" size="small" color="default" />}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>
          {isSOBEBRA && project.status === 'review' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setOpenReviewDialog(true)}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
            >
              Approuver
            </Button>
          )}
          {(isAdmin || isAgence) && !project.is_archived && (
            <Button
              variant="outlined"
              startIcon={<ArchiveIcon />}
              onClick={() => setOpenArchiveDialog(true)}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
            >
              Archiver
            </Button>
          )}
        </Box>
      </Box>

      {/* Informations Générales */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Description
            </Typography>
            <Typography variant="body1" paragraph>
              {project.description}
            </Typography>
            {project.objectives && (
              <>
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
                  Objectifs
                </Typography>
                <Typography variant="body1">{project.objectives}</Typography>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Informations
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Manager Agence
              </Typography>
              <Typography variant="body1" gutterBottom>
                {project.manager_agence_name || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Contact SOBEBRA
              </Typography>
              <Typography variant="body1" gutterBottom>
                {project.contact_soebra_name || '-'}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Assigné à
                  </Typography>
                  {(isAdmin || isAgence) && (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/projects/${project.id}/participants`)}
                    >
                      Gérer
                    </Button>
                  )}
                </Box>
                <Typography variant="body1" gutterBottom>
                  {project.assigned_to_name || 'Non assigné'}
                </Typography>
              </Box>
              {project.start_date && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Date de début
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(project.start_date).toLocaleDateString('fr-FR')}
                  </Typography>
                </>
              )}
              {project.due_date && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Date limite
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(project.due_date).toLocaleDateString('fr-FR')}
                  </Typography>
                </>
              )}
              {project.budget && (isAdmin || isAgence) && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Budget
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {project.budget.toLocaleString('fr-FR')} FCFA
                  </Typography>
                </>
              )}
              {project.zones && project.zones.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Zones
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {project.zones.map(zone => (
                      <Chip key={zone} label={zone} size="small" />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Onglets */}
      <Paper>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Tâches" icon={<CheckCircleIcon />} iconPosition="start" />
          <Tab label="Rapports" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Sondages" icon={<PollIcon />} iconPosition="start" />
          <Tab label="Inventaire" icon={<InventoryIcon />} iconPosition="start" />
          <Tab label="Messages" icon={<MessageIcon />} iconPosition="start" />
          <Tab label="Historique" icon={<HistoryIcon />} iconPosition="start" />
          {(isAdmin || isSOBEBRA) && <Tab label="Revue SOBEBRA" icon={<CheckCircleIcon />} iconPosition="start" />}
        </Tabs>

        {/* Onglet Tâches */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            {/* Progression */}
            <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Progression des Tâches
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {taskProgress.done} / {taskProgress.total} terminées
                    </Typography>
                    <Typography variant="body2">
                      {taskProgress.total > 0 ? Math.round((taskProgress.done / taskProgress.total) * 100) : 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={taskProgress.total > 0 ? (taskProgress.done / taskProgress.total) * 100 : 0}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Chip label={`${taskProgress.todo} À faire`} size="small" />
                  <Chip label={`${taskProgress.inProgress} En cours`} size="small" color="primary" />
                  <Chip label={`${taskProgress.done} Terminées`} size="small" color="success" />
                </Box>
              </CardContent>
            </Card>

            {(isAdmin || isAgence) && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenTaskDialog(true)}
                sx={{ mb: 3 }}
              >
                Ajouter une tâche
              </Button>
            )}

            {/* Liste des tâches */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {tasks.map(task => (
                <Accordion key={task.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Chip
                        label={getTaskStatusLabel(task.status)}
                        size="small"
                        color={getStatusColor(task.status) as any}
                      />
                      <Typography variant="subtitle1" sx={{ flex: 1 }}>
                        {task.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.assigned_to_name}
                      </Typography>
                      {task.due_date && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(task.due_date).toLocaleDateString('fr-FR')}
                        </Typography>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {task.description && (
                        <Typography variant="body2" paragraph>
                          {task.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Statut</InputLabel>
                          <Select
                            value={task.status}
                            onChange={(e) => handleUpdateTaskStatus(task.id, e.target.value as ProjectTask['status'])}
                            label="Statut"
                          >
                            <MenuItem value="todo">À faire</MenuItem>
                            <MenuItem value="in_progress">En cours</MenuItem>
                            <MenuItem value="review">En revue</MenuItem>
                            <MenuItem value="done">Terminée</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          size="small"
                          startIcon={<CommentIcon />}
                          onClick={() => {
                            setSelectedTask(task);
                            setOpenCommentDialog(true);
                          }}
                        >
                          Commenter ({task.comments?.length || 0})
                        </Button>
                        <Button size="small" startIcon={<AttachFileIcon />}>
                          Fichier ({task.attachments?.length || 0})
                        </Button>
                      </Box>
                      {task.comments && task.comments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Commentaires
                          </Typography>
                          {task.comments.map(comment => (
                            <Box key={comment.id} sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Avatar sx={{ width: 24, height: 24 }}>
                                  {comment.user_name.charAt(0)}
                                </Avatar>
                                <Typography variant="caption" fontWeight="bold">
                                  {comment.user_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(comment.created_at).toLocaleString('fr-FR')}
                                </Typography>
                              </Box>
                              <Typography variant="body2">{comment.content}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        )}

        {/* Onglet Rapports */}
        {tabValue === 1 && (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Rapports d'Activité Associés
              </Typography>
              {(isAdmin || isAgence || hasPermission('reports:create')) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setReportData({
                      title: '',
                      description: '',
                      location: '',
                      latitude: '',
                      longitude: '',
                      status: 'draft',
                    });
                    setReportFiles([]);
                    setOpenReportDialog(true);
                  }}
                  fullWidth={isMobile}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Ajouter un rapport
                </Button>
              )}
            </Box>
            {projectReports.length === 0 ? (
              <Alert severity="info">Aucun rapport associé à ce projet</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Titre</TableCell>
                      <TableCell>Créé par</TableCell>
                      <TableCell>Localisation</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectReports.map(report => (
                      <TableRow key={report.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2">{report.title}</Typography>
                        </TableCell>
                        <TableCell>{report.created_by_name}</TableCell>
                        <TableCell>
                          {report.location ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOnIcon fontSize="small" color="action" />
                              <Typography variant="body2">{report.location}</Typography>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(report.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getReportStatusLabel(report.status)}
                            size="small"
                            color={getReportStatusColor(report.status) as any}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Voir le rapport">
                            <IconButton
                              size="small"
                              onClick={() => handleViewReport(report)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Télécharger">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReport(report)}
                            >
                              <GetAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(isAdmin || isAgence) && (
                            <Tooltip title="Modifier">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedReport(report);
                                  setReportData({
                                    title: report.title,
                                    description: report.description || '',
                                    location: report.location || '',
                                    latitude: report.latitude?.toString() || '',
                                    longitude: report.longitude?.toString() || '',
                                    status: report.status,
                                  });
                                  setOpenEditReportDialog(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Onglet Sondages */}
        {tabValue === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sondages Associés
            </Typography>
            {projectSurveys.length === 0 ? (
              <Alert severity="info">Aucun sondage associé à ce projet</Alert>
            ) : (
              <List>
                {projectSurveys.map(survey => (
                  <ListItem key={survey.id}>
                    <ListItemText
                      primary={survey.name}
                      secondary={`${survey.responses} réponses - ${survey.status}`}
                    />
                    <Button 
                      size="small" 
                      sx={{ ml: 2 }} 
                      onClick={() => navigate(`/survey-analytics?projectId=${project?.id}&surveyId=${survey.id}&formId=${survey.form_id}`)}
                    >
                      Analyser
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Onglet Inventaire */}
        {tabValue === 3 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Ressources Affectées
              </Typography>
              {(isAdmin || isAgence) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setInventoryData({ asset_id: '', location: '', notes: '' });
                    setOpenInventoryDialog(true);
                  }}
                >
                  Ajouter une ressource
                </Button>
              )}
            </Box>
            {projectInventory.length === 0 ? (
              <Alert severity="info">Aucune ressource affectée à ce projet</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nom</TableCell>
                      <TableCell>Catégorie</TableCell>
                      <TableCell>Localisation</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>QR Code</TableCell>
                      {(isAdmin || isAgence) && <TableCell align="right">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectInventory.map(item => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>
                          <Chip label={item.category || '-'} size="small" />
                        </TableCell>
                        <TableCell>{item.location}</TableCell>
                        <TableCell>
                          <Chip 
                            label={item.status === 'deployed' ? 'Déployé' : item.status === 'in_stock' ? 'En Stock' : item.status} 
                            size="small" 
                            color={item.status === 'deployed' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {item.qr_code}
                          </Typography>
                        </TableCell>
                        {(isAdmin || isAgence) && (
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedInventoryItem(item);
                                setInventoryData({
                                  asset_id: item.id,
                                  location: item.location || '',
                                  notes: '',
                                });
                                setOpenEditInventoryDialog(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setProjectInventory(projectInventory.filter(i => i.id !== item.id));
                                addActivity('inventory_removed', `Ressource "${item.name}" retirée du projet`);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Onglet Discussion */}
        {tabValue === 4 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Un groupe de messages a été automatiquement créé pour ce projet. Seuls les membres assignés au projet peuvent y participer.
            </Alert>
            <Button
              variant="contained"
              startIcon={<MessageIcon />}
              onClick={() => navigate('/chat')}
            >
              Ouvrir les Messages du Projet
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Participants du groupe : {project.manager_agence_name}, {project.contact_soebra_name}, {project.assigned_to_name}
            </Typography>
          </Box>
        )}

        {/* Onglet Historique */}
        {tabValue === 5 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Historique des Activités
            </Typography>
            <List>
              {activities.map(activity => (
                <ListItem key={activity.id}>
                  <ListItemText
                    primary={activity.details}
                    secondary={`${activity.user_name} - ${new Date(activity.timestamp).toLocaleString('fr-FR')}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Onglet Revue SOBEBRA */}
        {tabValue === 6 && (isAdmin || isSOBEBRA) && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Revue SOBEBRA
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Vue synthétique pour la validation finale du projet par SOBEBRA
            </Alert>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Livrables Clés
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText primary="Tâches terminées" secondary={`${taskProgress.done} / ${taskProgress.total}`} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Rapports soumis" secondary={`${projectReports.length} rapport(s)`} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Sondages complétés" secondary={`${projectSurveys.length} sondage(s)`} />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
            <TextField
              fullWidth
              label="Rapport Post-Mortem (Leçons Apprises)"
              multiline
              rows={6}
              value={postMortem}
              onChange={(e) => {
                setPostMortem(e.target.value);
                setProject({ ...project, post_mortem: e.target.value });
              }}
              placeholder="Documentez les succès, les échecs et les améliorations pour les projets futurs..."
              sx={{ mb: 2 }}
            />
            
            {/* Upload de fichiers */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Fichiers joints (optionnel)
              </Typography>
              <input
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                id="review-file-upload"
                multiple
                type="file"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setReviewFiles(files);
                }}
              />
              <label htmlFor="review-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AttachFileIcon />}
                  sx={{ mr: 1 }}
                >
                  Ajouter des fichiers
                </Button>
              </label>
              {reviewFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {reviewFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      onDelete={() => {
                        const newFiles = reviewFiles.filter((_, i) => i !== index);
                        setReviewFiles(newFiles);
                      }}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setOpenReviewDialog(true)}
            >
              Valider le Projet
            </Button>
          </Box>
        )}
      </Paper>

      {/* Dialog Création Tâche */}
      <Dialog open={openTaskDialog} onClose={() => setOpenTaskDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter une tâche</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Titre"
            margin="normal"
            required
            value={taskData.title}
            onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={taskData.description}
            onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Assigné à</InputLabel>
            <Select
              value={taskData.assigned_to}
              onChange={(e) => setTaskData({ ...taskData, assigned_to: e.target.value })}
            >
              {users.map((user: any) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Date limite"
            margin="normal"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={taskData.due_date}
            onChange={(e) => setTaskData({ ...taskData, due_date: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTaskDialog(false)}>Annuler</Button>
          <Button onClick={handleCreateTask} variant="contained" disabled={!taskData.title}>
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Commentaire */}
      <Dialog 
        open={openCommentDialog} 
        onClose={() => setOpenCommentDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Ajouter un commentaire</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Tâche: {selectedTask.title}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Commentaire"
            margin="normal"
            multiline
            rows={4}
            value={commentData.content}
            onChange={(e) => setCommentData({ content: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCommentDialog(false)}>Annuler</Button>
          <Button onClick={handleAddComment} variant="contained" disabled={!commentData.content}>
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Revue SOBEBRA */}
      <Dialog 
        open={openReviewDialog} 
        onClose={() => setOpenReviewDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Valider le Projet</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cette action marquera le projet comme terminé et approuvé par SOBEBRA.
          </Alert>
          <Typography variant="body1">
            Êtes-vous sûr de vouloir valider ce projet ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReviewDialog(false)}>Annuler</Button>
          <Button onClick={handleApproveProject} variant="contained" color="success">
            Valider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Archivage */}
      <Dialog 
        open={openArchiveDialog} 
        onClose={() => setOpenArchiveDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Archiver le Projet</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            L'archivage rendra le projet non-modifiable mais le conservera dans la base de données.
          </Alert>
          <Typography variant="body1">
            Êtes-vous sûr de vouloir archiver ce projet ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenArchiveDialog(false)}>Annuler</Button>
          <Button onClick={handleArchiveProject} variant="contained" color="warning">
            Archiver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ajouter Inventaire */}
      <Dialog 
        open={openInventoryDialog} 
        onClose={() => setOpenInventoryDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Ajouter une ressource au projet</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Ressource</InputLabel>
              <Select
                label="Ressource"
                value={inventoryData.asset_id}
                onChange={(e) => setInventoryData({ ...inventoryData, asset_id: e.target.value })}
              >
                {availableAssets
                  .filter(asset => !asset.current_project_id || asset.current_project_id === project?.id)
                  .map((asset) => (
                    <MenuItem key={asset.id} value={asset.id}>
                      {asset.name} ({asset.status === 'in_stock' ? 'En Stock' : asset.status})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Localisation"
              margin="normal"
              value={inventoryData.location}
              onChange={(e) => setInventoryData({ ...inventoryData, location: e.target.value })}
              placeholder="Ex: Marché Dantokpa, Cotonou"
            />
            <TextField
              fullWidth
              label="Notes (optionnel)"
              margin="normal"
              multiline
              rows={3}
              value={inventoryData.notes}
              onChange={(e) => setInventoryData({ ...inventoryData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInventoryDialog(false)}>Annuler</Button>
          <Button
            onClick={() => {
              if (inventoryData.asset_id) {
                const asset = availableAssets.find(a => a.id === inventoryData.asset_id);
                if (asset) {
                  const newItem = {
                    id: asset.id,
                    name: asset.name,
                    location: inventoryData.location || asset.location || '-',
                    status: 'deployed',
                    category: asset.category,
                    qr_code: asset.qr_code,
                  };
                  setProjectInventory([...projectInventory, newItem]);
                  addActivity('inventory_added', `Ressource "${asset.name}" ajoutée au projet`);
                  setOpenInventoryDialog(false);
                  setInventoryData({ asset_id: '', location: '', notes: '' });
                }
              }
            }}
            variant="contained"
            disabled={!inventoryData.asset_id}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Modifier Inventaire */}
      <Dialog 
        open={openEditInventoryDialog} 
        onClose={() => setOpenEditInventoryDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Modifier la ressource</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nom"
              margin="normal"
              value={selectedInventoryItem?.name || ''}
              InputProps={{ readOnly: true }}
            />
            <TextField
              fullWidth
              label="Localisation"
              margin="normal"
              value={inventoryData.location}
              onChange={(e) => setInventoryData({ ...inventoryData, location: e.target.value })}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Statut</InputLabel>
              <Select
                value={selectedInventoryItem?.status || 'deployed'}
                onChange={(e) => {
                  if (selectedInventoryItem) {
                    const updated = projectInventory.map(item =>
                      item.id === selectedInventoryItem.id
                        ? { ...item, status: e.target.value }
                        : item
                    );
                    setProjectInventory(updated);
                    setSelectedInventoryItem({ ...selectedInventoryItem, status: e.target.value });
                  }
                }}
                label="Statut"
              >
                <MenuItem value="in_stock">En Stock</MenuItem>
                <MenuItem value="deployed">Déployé</MenuItem>
                <MenuItem value="in_repair">En Réparation</MenuItem>
                <MenuItem value="damaged">Endommagé</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes (optionnel)"
              margin="normal"
              multiline
              rows={3}
              value={inventoryData.notes}
              onChange={(e) => setInventoryData({ ...inventoryData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditInventoryDialog(false)}>Annuler</Button>
          <Button
            onClick={() => {
              if (selectedInventoryItem) {
                const updated = projectInventory.map(item =>
                  item.id === selectedInventoryItem.id
                    ? { ...item, location: inventoryData.location, status: selectedInventoryItem.status }
                    : item
                );
                setProjectInventory(updated);
                addActivity('inventory_updated', `Ressource "${selectedInventoryItem.name}" modifiée`);
                setOpenEditInventoryDialog(false);
                setSelectedInventoryItem(null);
              }
            }}
            variant="contained"
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Créer Rapport */}
      <Dialog 
        open={openReportDialog} 
        onClose={() => setOpenReportDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Ajouter un Rapport d'Activité</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Titre du rapport"
              margin="normal"
              required
              value={reportData.title}
              onChange={(e) => setReportData({ ...reportData, title: e.target.value })}
            />
            <TextField
              fullWidth
              label="Description"
              margin="normal"
              multiline
              rows={4}
              value={reportData.description}
              onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Localisation
              </Typography>
              <LocationPicker
                latitude={reportData.latitude ? parseFloat(reportData.latitude) : null}
                longitude={reportData.longitude ? parseFloat(reportData.longitude) : null}
                location={reportData.location}
                onLocationChange={(lat, lng, address) => {
                  setReportData({
                    ...reportData,
                    latitude: lat?.toString() || '',
                    longitude: lng?.toString() || '',
                    location: address || '',
                  });
                }}
                height="300px"
              />
            </Box>
            <FormControl fullWidth margin="normal">
              <InputLabel>Statut</InputLabel>
              <Select
                value={reportData.status}
                onChange={(e) => setReportData({ ...reportData, status: e.target.value })}
                label="Statut"
              >
                <MenuItem value="draft">Brouillon</MenuItem>
                <MenuItem value="submitted">Soumis</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Pièces jointes (optionnel)
              </Typography>
              <input
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                id="report-file-upload"
                multiple
                type="file"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setReportFiles(files);
                }}
              />
              <label htmlFor="report-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AttachFileIcon />}
                  sx={{ mr: 1 }}
                >
                  Ajouter des fichiers
                </Button>
              </label>
              {reportFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {reportFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      onDelete={() => {
                        const newFiles = reportFiles.filter((_, i) => i !== index);
                        setReportFiles(newFiles);
                      }}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReportDialog(false)}>Annuler</Button>
          <Button onClick={handleCreateReport} variant="contained" disabled={!reportData.title}>
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Modifier Rapport */}
      <Dialog 
        open={openEditReportDialog} 
        onClose={() => setOpenEditReportDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Modifier le Rapport</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Titre du rapport"
              margin="normal"
              required
              value={reportData.title}
              onChange={(e) => setReportData({ ...reportData, title: e.target.value })}
            />
            <TextField
              fullWidth
              label="Description"
              margin="normal"
              multiline
              rows={4}
              value={reportData.description}
              onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Localisation
              </Typography>
              <LocationPicker
                latitude={reportData.latitude ? parseFloat(reportData.latitude) : null}
                longitude={reportData.longitude ? parseFloat(reportData.longitude) : null}
                location={reportData.location}
                onLocationChange={(lat, lng, address) => {
                  setReportData({
                    ...reportData,
                    latitude: lat?.toString() || '',
                    longitude: lng?.toString() || '',
                    location: address || '',
                  });
                }}
                height="300px"
              />
            </Box>
            <FormControl fullWidth margin="normal">
              <InputLabel>Statut</InputLabel>
              <Select
                value={reportData.status}
                onChange={(e) => setReportData({ ...reportData, status: e.target.value })}
                label="Statut"
              >
                <MenuItem value="draft">Brouillon</MenuItem>
                <MenuItem value="submitted">Soumis</MenuItem>
                <MenuItem value="pre_validated">Pré-validé</MenuItem>
                {(isAdmin || isSOBEBRA) && (
                  <>
                    <MenuItem value="approved">Approuvé</MenuItem>
                    <MenuItem value="rejected">Rejeté</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Pièces jointes (optionnel)
              </Typography>
              <input
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                id="edit-report-file-upload"
                multiple
                type="file"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setReportFiles(files);
                }}
              />
              <label htmlFor="edit-report-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AttachFileIcon />}
                  sx={{ mr: 1 }}
                >
                  Ajouter des fichiers
                </Button>
              </label>
              {reportFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {reportFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      onDelete={() => {
                        const newFiles = reportFiles.filter((_, i) => i !== index);
                        setReportFiles(newFiles);
                      }}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditReportDialog(false)}>Annuler</Button>
          <Button onClick={handleUpdateReport} variant="contained" disabled={!reportData.title}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Voir Rapport */}
      <Dialog
        open={openViewReportDialog}
        onClose={() => setOpenViewReportDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{selectedReport?.title}</Typography>
            {selectedReport && (
              <Chip
                label={getReportStatusLabel(selectedReport.status)}
                size="small"
                color={getReportStatusColor(selectedReport.status) as any}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Créé par
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedReport.created_by_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Date de création
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(selectedReport.created_at).toLocaleString('fr-FR')}
                  </Typography>
                </Grid>
                {selectedReport.location && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Localisation
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon color="action" />
                      <Typography variant="body1">{selectedReport.location}</Typography>
                    </Box>
                    {selectedReport.latitude && selectedReport.longitude && (
                      <Typography variant="caption" color="textSecondary" sx={{ ml: 4 }}>
                        Coordonnées: {selectedReport.latitude}, {selectedReport.longitude}
                      </Typography>
                    )}
                  </Grid>
                )}
                {selectedReport.description && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedReport.description}
                    </Typography>
                  </Grid>
                )}
                {selectedReport.submitted_at && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Soumis le
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedReport.submitted_at).toLocaleString('fr-FR')}
                    </Typography>
                  </Grid>
                )}
                {selectedReport.pre_validated_by_name && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Pré-validé par
                    </Typography>
                    <Typography variant="body1">
                      {selectedReport.pre_validated_by_name}
                      {selectedReport.pre_validated_at && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          Le {new Date(selectedReport.pre_validated_at).toLocaleString('fr-FR')}
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                )}
                {selectedReport.approved_by_name && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Approuvé par
                    </Typography>
                    <Typography variant="body1">
                      {selectedReport.approved_by_name}
                      {selectedReport.approved_at && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          Le {new Date(selectedReport.approved_at).toLocaleString('fr-FR')}
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                )}
                {selectedReport.attachments && selectedReport.attachments.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Pièces jointes
                    </Typography>
                    <List>
                      {selectedReport.attachments.map((attachment: any, index: number) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <AttachFileIcon />
                          </ListItemIcon>
                          <ListItemText primary={attachment.filename || attachment.name} />
                          <Button size="small" startIcon={<GetAppIcon />}>
                            Télécharger
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewReportDialog(false)}>Fermer</Button>
          {selectedReport && (
            <Button
              variant="contained"
              startIcon={<GetAppIcon />}
              onClick={() => handleDownloadReport(selectedReport)}
            >
              Télécharger
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetail;
